import { useEffect, useState } from 'react';

/**
 * Sub-routes that live under `/my-packs`. Each maps to one screen of
 * the My Packs flow.
 *
 *   /my-packs                       → { kind: 'index' }     — tabs page
 *   /my-packs/deliveries/new        → { kind: 'delivery-new' } — create form
 *   /my-packs/deliveries/:id        → { kind: 'delivery-show', id }
 *
 * The top-level view in `viewURL.ts` still resolves to `'my-packs'`
 * for every prefix above — the shell mounts MyPacksPage which then
 * drives this internal router.
 */
export type MyPacksRoute =
  | { kind: 'index' }
  | { kind: 'delivery-new' }
  | { kind: 'delivery-show'; id: number };

const INDEX_PATH = '/my-packs';
const DELIVERIES_NEW_PATH = '/my-packs/deliveries/new';
const DELIVERY_SHOW_PATTERN = /^\/my-packs\/deliveries\/(\d+)$/;

export function parseMyPacksRoute(pathname: string): MyPacksRoute {
  if (pathname === DELIVERIES_NEW_PATH) return { kind: 'delivery-new' };
  const showMatch = DELIVERY_SHOW_PATTERN.exec(pathname);
  if (showMatch && showMatch[1]) {
    const id = Number(showMatch[1]);
    if (Number.isFinite(id) && id > 0) return { kind: 'delivery-show', id };
  }
  return { kind: 'index' };
}

export function myPacksRouteToPath(route: MyPacksRoute): string {
  switch (route.kind) {
    case 'delivery-new':
      return DELIVERIES_NEW_PATH;
    case 'delivery-show':
      return `/my-packs/deliveries/${route.id}`;
    case 'index':
    default:
      return INDEX_PATH;
  }
}

function writeHistory(route: MyPacksRoute, mode: 'push' | 'replace'): void {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  url.pathname = myPacksRouteToPath(route);
  // Strip the legacy `?view=…` query so URL state has a single source.
  url.searchParams.delete('view');
  if (mode === 'push') window.history.pushState(null, '', url);
  else window.history.replaceState(null, '', url);
  // Notify the design-constructor preview iframe (mirrors viewURL.ts).
  if (window.parent !== window) {
    window.parent.postMessage(
      { type: 'design-constructor-route', pathname: url.pathname },
      '*',
    );
  }
}

export function pushMyPacksRoute(route: MyPacksRoute): void {
  writeHistory(route, 'push');
}

export function replaceMyPacksRoute(route: MyPacksRoute): void {
  writeHistory(route, 'replace');
}

/**
 * React hook that mirrors the current `/my-packs/...` route. Updates
 * automatically on browser back/forward (popstate) and exposes
 * navigation helpers that also update the URL.
 */
export function useMyPacksRoute(): {
  route: MyPacksRoute;
  push: (route: MyPacksRoute) => void;
  replace: (route: MyPacksRoute) => void;
} {
  const [route, setRoute] = useState<MyPacksRoute>(() =>
    typeof window === 'undefined'
      ? { kind: 'index' }
      : parseMyPacksRoute(window.location.pathname),
  );

  useEffect(() => {
    const handler = () => setRoute(parseMyPacksRoute(window.location.pathname));
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, []);

  return {
    route,
    push: (next) => {
      pushMyPacksRoute(next);
      setRoute(next);
    },
    replace: (next) => {
      replaceMyPacksRoute(next);
      setRoute(next);
    },
  };
}
